import React, { useState, useEffect } from 'react';
import { FaChartLine, FaChild, FaCalendarAlt, FaFileAlt, FaTasks, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDate } from '../../utils/helpers';

/**
 * Child progress component for parents
 * Displays academic progress, reports, and tasks for children
 */
const ChildProgress = () => {
  const location = useLocation();
  const { showError } = useToast();
  
  // Children and selection state
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Progress data states
  const [progressData, setProgressData] = useState({
    academicPerformance: [],
    tasks: [],
    reports: [],
    attendance: {}
  });
  
  // UI state for expandable sections
  const [expandedSections, setExpandedSections] = useState({
    performance: true,
    tasks: true,
    reports: true,
    attendance: true
  });

  // Fetch children on component mount
  useEffect(() => {
    fetchChildren();
  }, []);
  
  // Set selected child based on URL params or first child
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const childId = params.get('childId');
    
    if (children.length > 0) {
      if (childId) {
        const child = children.find(c => c._id === childId);
        if (child) {
          setSelectedChild(child);
          fetchChildProgress(childId);
        } else {
          setSelectedChild(children[0]);
          fetchChildProgress(children[0]._id);
        }
      } else {
        setSelectedChild(children[0]);
        fetchChildProgress(children[0]._id);
      }
    }
  }, [children, location]);

  // Fetch children list from API
  const fetchChildren = async () => {
    try {
      setLoading(true);
      const response = await api.get('/parents/children');
      setChildren(response.data.data || []);
    } catch (error) {
      console.error('Error fetching children:', error);
      showError('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  // Fetch selected child's progress data
  const fetchChildProgress = async (childId) => {
    try {
      setLoading(true);
      const response = await api.get(`/parents/children/${childId}/progress`);
      setProgressData(response.data.data || {
        academicPerformance: [],
        tasks: [],
        reports: [],
        attendance: {}
      });
    } catch (error) {
      console.error('Error fetching child progress:', error);
      showError('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  // Handle child selection change
  const handleChildChange = (e) => {
    const childId = e.target.value;
    const child = children.find(c => c._id === childId);
    setSelectedChild(child);
    fetchChildProgress(childId);
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Get color class based on performance score
  const getScoreColorClass = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  // Get status badge for tasks
  const getTaskStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Completed</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">In Progress</span>;
      case 'overdue':
        return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Overdue</span>;
      case 'pending':
      default:
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Pending</span>;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaChartLine className="mr-2 text-primary" /> Child Progress
          </h1>
          <p className="text-gray-600 mt-1">
            Track your child's academic performance and progress
          </p>
        </div>
      </div>
      
      {/* Child Selection Dropdown */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-center">
          <div className="flex items-center mr-4 mb-4 sm:mb-0">
            <FaChild className="text-primary mr-2" />
            <h2 className="text-lg font-medium">Select Child</h2>
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={selectedChild?._id || ''}
              onChange={handleChildChange}
              className="form-select w-full"
              disabled={children.length === 0 || loading}
            >
              {children.map(child => (
                <option key={child._id} value={child._id}>
                  {child.firstName} {child.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : children.length === 0 ? (
        <div className="bg-white rounded-lg shadow-soft p-8 text-center">
          <FaChild className="mx-auto text-gray-300 text-5xl mb-4" />
          <h3 className="text-xl font-medium text-gray-600 mb-2">No Children Added</h3>
          <p className="text-gray-500 mb-4">
            Add your child first to track their progress
          </p>
          <a href="/parent/students" className="btn-primary">
            Add Child
          </a>
        </div>
      ) : (
        <>
          {/* Academic Performance Section */}
          <div className="bg-white rounded-lg shadow-soft mb-6 overflow-hidden">
            <div 
              className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection('performance')}
            >
              <h2 className="font-semibold text-lg flex items-center">
                <FaChartLine className="mr-2 text-primary" /> Academic Performance
              </h2>
              <button className="text-gray-500">
                {expandedSections.performance ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
            
            {expandedSections.performance && (
              <div className="p-6">
                {progressData.academicPerformance.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No academic performance data available
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Term</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {progressData.academicPerformance.map((subject, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-medium text-gray-900">{subject.subject}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`font-bold ${getScoreColorClass(subject.score)}`}>{subject.score}%</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-medium">{subject.grade}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{subject.term}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{formatDate(subject.date)}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-500">{subject.comments || '-'}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Tasks & Assignments Section */}
          <div className="bg-white rounded-lg shadow-soft mb-6 overflow-hidden">
            <div 
              className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection('tasks')}
            >
              <h2 className="font-semibold text-lg flex items-center">
                <FaTasks className="mr-2 text-primary" /> Tasks & Assignments
              </h2>
              <button className="text-gray-500">
                {expandedSections.tasks ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
            
            {expandedSections.tasks && (
              <div className="p-6">
                {progressData.tasks.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No tasks or assignments available
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {progressData.tasks.map((task, index) => (
                      <div 
                        key={index} 
                        className="border border-gray-200 rounded-lg p-4 hover:border-primary transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <h3 className="font-medium text-gray-900">{task.title}</h3>
                          {getTaskStatusBadge(task.status)}
                        </div>
                        <p className="text-sm text-gray-500 mt-2">{task.description}</p>
                        <div className="flex flex-wrap mt-3 text-xs text-gray-500 space-x-4">
                          <span className="flex items-center">
                            <FaCalendarAlt className="mr-1" /> Due: {formatDate(task.dueDate)}
                          </span>
                          <span>Subject: {task.subject}</span>
                          {task.assignedBy && (
                            <span>Assigned by: {task.assignedBy}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Reports Section */}
          <div className="bg-white rounded-lg shadow-soft mb-6 overflow-hidden">
            <div 
              className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection('reports')}
            >
              <h2 className="font-semibold text-lg flex items-center">
                <FaFileAlt className="mr-2 text-primary" /> Reports & Documents
              </h2>
              <button className="text-gray-500">
                {expandedSections.reports ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
            
            {expandedSections.reports && (
              <div className="p-6">
                {progressData.reports.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No reports or documents available
                  </p>
                ) : (
                  <div className="space-y-4">
                    {progressData.reports.map((report, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors"
                      >
                        <div>
                          <h3 className="font-medium text-gray-900">{report.title}</h3>
                          <p className="text-sm text-gray-500">
                            {report.type} • {formatDate(report.date)}
                          </p>
                        </div>
                        <a 
                          href={report.fileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-primary-outline px-4 py-2 text-sm"
                        >
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Attendance Section */}
          <div className="bg-white rounded-lg shadow-soft mb-6 overflow-hidden">
            <div 
              className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection('attendance')}
            >
              <h2 className="font-semibold text-lg flex items-center">
                <FaCalendarAlt className="mr-2 text-primary" /> Attendance
              </h2>
              <button className="text-gray-500">
                {expandedSections.attendance ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
            
            {expandedSections.attendance && (
              <div className="p-6">
                {!progressData.attendance || Object.keys(progressData.attendance).length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No attendance data available
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h3 className="font-medium text-green-700 mb-1">Present Days</h3>
                      <p className="text-2xl font-bold text-green-600">{progressData.attendance.presentDays || 0}</p>
                      <p className="text-sm text-green-600">
                        {progressData.attendance.presentPercentage || 0}% Attendance Rate
                      </p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h3 className="font-medium text-yellow-700 mb-1">Absent Days</h3>
                      <p className="text-2xl font-bold text-yellow-600">{progressData.attendance.absentDays || 0}</p>
                      <p className="text-sm text-yellow-600">
                        {progressData.attendance.absentDates?.length > 0 ? 
                          `Last absent: ${formatDate(progressData.attendance.absentDates[0])}` : 
                          'No absences recorded'}
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-medium text-blue-700 mb-1">School Days</h3>
                      <p className="text-2xl font-bold text-blue-600">{progressData.attendance.totalDays || 0}</p>
                      <p className="text-sm text-blue-600">
                        Current Term: {progressData.attendance.term || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChildProgress;
